import DescriptionIcon from "@mui/icons-material/Description";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
	Box,
	Chip,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useState } from "react";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../shared/utils";
import { deriveLivveUiStatus, LivveInvoiceRow, LivveUiStatus } from "../../../shared/services/api/comission/ComissionServices";
import type { LivveMenuModalType } from "./Menus/LivveMenu";
import { LivveMenu } from "./Menus/LivveMenu";
import { ModalDetailLivve } from "./Modals/ModalDetailLivve";

const statusLabel: Record<LivveUiStatus, string> = {
	ready: "Pronta pra enviar",
	sent: "Enviada",
	paid: "Paga",
};

const statusColor: Record<LivveUiStatus, { bg: string; text: string }> = {
	ready: { bg: "#f3f4f6", text: "#374151" }, // mesmo cinza neutro usado pro status "open" da comissão
	sent: { bg: "#eef2ff", text: "#4338ca" }, // roxo — etapa de processamento, mesma leitura do fluxo desenhado
	paid: { bg: "#e9f8f5", text: "#0f6e56" }, // mesmo verde usado pro status "paid" da comissão
};

interface Props {
	rows: LivveInvoiceRow[];
	totalCount: number;
	page: number;
	rowsPerPage: number;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (value: number) => void;
	onClickSend: (row: LivveInvoiceRow) => void;
	onClickConfirmPayment: (row: LivveInvoiceRow) => void;
}

export const TableLivve = ({ rows, totalCount, page, rowsPerPage, onPageChange, onRowsPerPageChange, onClickSend, onClickConfirmPayment }: Props) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const [selectedRow, setSelectedRow] = useState<LivveInvoiceRow | null>(null);
	const [modalType, setModalType] = useState<LivveMenuModalType | null>(null);

	const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: LivveInvoiceRow) => {
		setAnchorEl(event.currentTarget);
		setSelectedRow(row);
	};

	const handleMenuClose = () => setAnchorEl(null);

	const handleCloseModal = () => {
		setSelectedRow(null);
		setModalType(null);
	};

	const handleOpenModal = (type: LivveMenuModalType, row: LivveInvoiceRow) => {
		if (type === "send") {
			onClickSend(row);
			return;
		}
		if (type === "confirmPayment") {
			onClickConfirmPayment(row);
			return;
		}
		setSelectedRow(row);
		setModalType(type);
	};

	return (
		<TableContainer
			component={Paper}
			variant="elevation"
			sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "hidden" }}
		>
			<Table size="small" stickyHeader aria-label="Faturas Livve">
				<TableHead>
					<TableRow sx={{ backgroundColor: "grey.100" }}>
						{["Imobiliária", "Referência", "Valor", "Vencimento", "Status", "Ações"].map((header, i) => (
							<TableCell key={i} align={i === 2 ? "center" : i === 5 ? "right" : "left"}>
								<Typography variant="button">{header}</Typography>
							</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.map((row) => {
						const uiStatus = deriveLivveUiStatus(row);
						return (
							<TableRow key={row.id}>
								<TableCell>
									<Stack spacing={0.5}>
										<Typography fontWeight="bold">{row.clientName}</Typography>
										<Typography variant="body2" color="textSecondary">
											{globalMaskDocument(row.clientDocument)}
										</Typography>
									</Stack>
								</TableCell>
								<TableCell>{row.reference}</TableCell>
								<TableCell align="center">
									<Typography fontWeight={500} color="textSecondary">
										{globalFormatCurrencyBRL(row.totValue)}
									</Typography>
								</TableCell>
								<TableCell>{row.dueDate ? format(new Date(row.dueDate), "dd/MM/yyyy") : "-"}</TableCell>
								<TableCell>
									<Chip
										label={statusLabel[uiStatus]}
										size="small"
										variant="filled"
										sx={{ backgroundColor: statusColor[uiStatus].bg, color: statusColor[uiStatus].text, fontWeight: "normal" }}
									/>
									{row.livveOrderId && (
										<Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
											Pedido #{row.livveOrderId}
											{row.livveOrderStatus ? ` · ${row.livveOrderStatus}` : ""}
										</Typography>
									)}
									{row.livveBoletoUrl && (
										<Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
											<a href={row.livveBoletoUrl} target="_blank" rel="noreferrer">
												<Stack direction="row" spacing={0.5} alignItems="center" component="span">
													<DescriptionIcon sx={{ fontSize: 14 }} />
													<span>Boleto</span>
												</Stack>
											</a>
										</Typography>
									)}
								</TableCell>
								<TableCell align="right">
									<IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
										<MoreVertIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						);
					})}
					{rows.length === 0 && (
						<TableRow>
							<TableCell colSpan={6}>
								<Typography variant="body2" color="textSecondary" textAlign="center" py={3}>
									Nenhum registro encontrado.
								</Typography>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>

			<Box display="flex" justifyContent="flex-end" alignItems="center" mt={2} pr={2}>
				<TablePagination
					component="div"
					count={totalCount}
					page={page}
					rowsPerPage={rowsPerPage}
					onPageChange={(_, newPage) => onPageChange(newPage)}
					onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
					rowsPerPageOptions={[10, 25, 50]}
					labelRowsPerPage="Linhas por página:"
					labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
				/>
			</Box>

			<LivveMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} selectedRow={selectedRow} onOpenModal={handleOpenModal} />

			{selectedRow && modalType === "details" && <ModalDetailLivve open={true} onClose={handleCloseModal} row={selectedRow} />}
		</TableContainer>
	);
};
